import React from "react";
import {
  FileText,
  Users,
  List,
  CheckCircle,
  AlertCircle,
  Info,
  ArrowRight,
  Settings,
  TestTube,
  Server,
  User,
} from "lucide-react";

interface WF_Repayment_Deferral_Constitution_Wise_Use_CaseProps {}

const WF_Repayment_Deferral_Constitution_Wise_Use_Case: React.FC<WF_Repayment_Deferral_Constitution_Wise_Use_CaseProps> = () => {
  return (
    <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 bg-white font-sans">
      {/* Heading */}
      <h1 className="text-2xl font-bold text-gray-900 mb-6 border-b border-gray-300 pb-2">
        Repayment Deferral Constitution Wise
      </h1>
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-8">
        {/* Description */}
        <section className="mb-8">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-300 pb-2">
            <Info size={18} className="mr-2 text-blue-600" />
            Description
          </h2>
          <p className="text-gray-700 leading-relaxed">
            Repayment deferral is a process where customers can postpone their
            EMI payments for a specific period, typically during special
            circumstances like festivals. Constitution Wise Deferral allows
            banks to process deferral requests in bulk, based on customer
            constitution (e.g., SME). Users can apply deferral rules based on
            constitution, which displays all relevant finances under that
            customer segment for selection.
          </p>
        </section>

        {/* Actors */}
        <section className="mb-8">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-300 pb-2">
            <Users size={18} className="mr-2 text-blue-600" />
            Actors
          </h2>
          <ul className="list-disc pl-5 text-gray-700">
            <li>User</li>
            <li>Checker</li>
          </ul>
        </section>

        {/* User Actions & System Responses */}
        <section className="mb-8">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-300 pb-2">
            <List size={18} className="mr-2 text-blue-600" />
            User Actions & System Responses
          </h2>
          <ol className="list-decimal pl-5 text-gray-700">
            <li>User initiates Constitution Based Repayment Deferral.</li>
            <li>
              User selects the constitution from the dropdown (e.g., SME).
            </li>
            <li>
              System displays all finance accounts under the selected
              constitution.
              <ul className="list-disc pl-5 mt-2">
                <li>System action: Retrieves and displays finance accounts.</li>
              </ul>
            </li>
            <li>User selects one or more finance accounts to defer.</li>
            <li>
              User inputs:
              <ul className="list-disc pl-5 mt-2">
                <li>Deferral Effective Date</li>
                <li>Number of Deferrals</li>
              </ul>
            </li>
            <li>User submits the deferral record.</li>
            <li>Checker reviews and authorizes the record if correct.</li>
            <li>
              System confirms deferral and sends notification to customers.
              <ul className="list-disc pl-5 mt-2">
                <li>
                  System action: Applies deferral and sends notifications.
                </li>
              </ul>
            </li>
          </ol>
        </section>

        {/* Precondition */}
        <section className="mb-8">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-300 pb-2">
            <CheckCircle size={18} className="mr-2 text-green-600" />
            Precondition
          </h2>
          <ul className="list-disc pl-5 text-gray-700">
            <li>
              Finance Account exists and a need for repayment deferral is
              identified.
            </li>
          </ul>
        </section>

        {/* Post Condition */}
        <section className="mb-8">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-300 pb-2">
            <CheckCircle size={18} className="mr-2 text-green-600" />
            Post Condition
          </h2>
          <ul className="list-disc pl-5 text-gray-700">
            <li>
              Deferral is applied to selected finance accounts based on
              constitution.
            </li>
          </ul>
        </section>

        {/* Straight Through Process (STP) */}
        <section className="mb-8">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-300 pb-2">
            <ArrowRight size={18} className="mr-2 text-blue-600" />
            Straight Through Process (STP)
          </h2>
          <p className="text-gray-700 leading-relaxed">
            Initiate → Select Constitution → Display Accounts → Input Deferral
            Details → Submit → Checker Authorization → Notify
          </p>
        </section>

        {/* Alternative Flows */}
        <section className="mb-8">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-300 pb-2">
            <ArrowRight size={18} className="mr-2 text-blue-600" />
            Alternative Flows
          </h2>
          <ul className="list-disc pl-5 text-gray-700">
            <li>
              If the selected constitution has no finance accounts, no data will
              be displayed.
            </li>
          </ul>
        </section>

        {/* Exception Flows */}
        <section className="mb-8">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-300 pb-2">
            <AlertCircle size={18} className="mr-2 text-red-600" />
            Exception Flows
          </h2>
          <ul className="list-disc pl-5 text-gray-700">
            <li>
              Discrepancy found by checker → Modification required → Resubmit
              for authorization.
            </li>
            <li>Missing or invalid data → System validation fails.</li>
          </ul>
        </section>

        {/* User Activity Diagram (Flowchart) */}
        <section className="mb-8">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-300 pb-2">
            <List size={18} className="mr-2 text-blue-600" />
            User Activity Diagram (Flowchart)
          </h2>
          <p className="text-gray-700 leading-relaxed">
            Start → Initiate Constitution Based Deferral → Select Constitution →
            Display Accounts → Select Accounts → Enter Deferral Info → Submit →
            Checker Verification → Authorization → Notify → End
          </p>
        </section>

        {/* Parking Lot */}
        <section className="mb-8">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-300 pb-2">
            <Info size={18} className="mr-2 text-blue-600" />
            Parking Lot
          </h2>
          <ul className="list-disc pl-5 text-gray-700">
            <li>
              Automated rule assignment for constitutions could reduce manual
              inputs.
            </li>
          </ul>
        </section>

        {/* System Components Involved */}
        <section className="mb-8">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-300 pb-2">
            <Settings size={18} className="mr-2 text-blue-600" />
            System Components Involved
          </h2>
          <ul className="list-disc pl-5 text-gray-700">
            <li>Deferral Processing Module</li>
            <li>Customer Segmentation Engine</li>
            <li>Notification Service</li>
          </ul>
        </section>

        {/* Test Scenarios */}
        <section className="mb-8">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-300 pb-2">
            <TestTube size={18} className="mr-2 text-blue-600" />
            Test Scenarios
          </h2>
          <ul className="list-disc pl-5 text-gray-700">
            <li>Select SME constitution and apply deferral to all accounts.</li>
            <li>
              Attempt submission with missing effective date (expect error).
            </li>
            <li>Checker rejects due to discrepancy, user resubmits.</li>
            <li>Notification sent upon successful authorization.</li>
          </ul>
        </section>

        {/* Infra & Deployment Notes */}
        <section className="mb-8">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-300 pb-2">
            <Server size={18} className="mr-2 text-blue-600" />
            Infra & Deployment Notes
          </h2>
          <ul className="list-disc pl-5 text-gray-700">
            <li>
              Ensure segment rules and constitution values are up to date.
            </li>
            <li>Maintain audit log for all submissions and approvals.</li>
          </ul>
        </section>

        {/* Dev Team Ownership */}
        <section className="mb-8">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4 flex items-center border-b border-gray-300 pb-2">
            <User size={18} className="mr-2 text-blue-600" />
            Dev Team Ownership
          </h2>
          <p className="text-gray-700 leading-relaxed">
            Squad: Repayment Management Team
            <br />
            Contact: Sunil Kumar
            <br />
            JIRA: DEF-CON-008
            <br />
            Git Repo: git.company.com/deferral-module
          </p>
        </section>
      </div>
    </main>
  );
};

export default WF_Repayment_Deferral_Constitution_Wise_Use_Case;